/**
 * The only place the browser talks to the platform.
 *
 * Two rules worth stating because they are the security model, not style: the CSRF header is sent on every
 * mutating request (the server refuses those without it), and every non-2xx response is read as a
 * `application/problem+json` document so the UI can show the server's own code, correlation id and
 * `nextAction` instead of inventing a message.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const CSRF_HEADER = 'X-Requested-By';
export const CSRF_VALUE = 'turbinepulse-app';

export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail: string;
  code?: string;
  requestId?: string;
  retryable?: boolean;
  nextAction?: { kind: string; label: string; href?: string };
  details?: Array<{ path: string; message: string }>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails;

  constructor(status: number, problem: ProblemDetails) {
    super(problem.detail || problem.title || `request failed with ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }

  get code(): string {
    return this.problem.code ?? 'ERROR';
  }

  get requestId(): string | undefined {
    return this.problem.requestId;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isEntitlement(): boolean {
    return this.status === 402 || this.code === 'ENTITLEMENT_REQUIRED';
  }
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export async function request<T>(path: string, init: { method?: string; body?: unknown; signal?: AbortSignal; headers?: Record<string, string> } = {}): Promise<T> {
  const method = init.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json', ...init.headers };
  if (MUTATING.has(method)) headers[CSRF_HEADER] = CSRF_VALUE;
  if (init.body !== undefined) headers['content-type'] = 'application/json';

  const res = await fetch(`/api/v1${path}`, {
    method,
    credentials: 'same-origin',
    signal: init.signal,
    headers,
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const parsed: unknown = text ? safeJson(text) : null;
  if (!res.ok) {
    const problem: ProblemDetails =
      parsed && typeof parsed === 'object' && 'status' in (parsed as Record<string, unknown>)
        ? (parsed as ProblemDetails)
        : { type: '/errors/unknown', title: res.statusText || 'Request failed', status: res.status, detail: text.slice(0, 400) || 'the server returned an unreadable error' };
    throw new ApiError(res.status, problem);
  }
  return (parsed ?? null) as T;
}

const safeJson = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

/**
 * Reads a list out of whatever a list endpoint returned: `{ rows, total }` from the paged endpoints, or a bare
 * array from the older ones. The UI must not call `.map` on the response body itself — that is how one panel on
 * /app/account took the whole application down, because the ledger route answers with an object while the type
 * said array, and `undefined.map` is not a state the product is allowed to have.
 */
export const rowsOf = <T>(payload: unknown): T[] => {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === 'object') {
    const rows = (payload as { rows?: unknown }).rows;
    if (Array.isArray(rows)) return rows as T[];
  }
  return [];
};

/** The companion total, when the endpoint reports one, so a table can say "3 of 41" rather than "3". */
export const totalOf = (payload: unknown, listed: number): number => {
  if (payload && typeof payload === 'object') {
    const total = (payload as { total?: unknown }).total;
    if (typeof total === 'number' && Number.isFinite(total)) return total;
  }
  return listed;
};

export const api = {
  get: <T,>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T,>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body: body ?? {} }),
  put: <T,>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body: body ?? {} }),
  patch: <T,>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body: body ?? {} }),
  del: <T,>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** Multipart upload: the CSRF header still applies, the body must not be JSON. */
  upload: async <T,>(path: string, form: FormData): Promise<T> => {
    const res = await fetch(`/api/v1${path}`, { method: 'POST', credentials: 'same-origin', headers: { [CSRF_HEADER]: CSRF_VALUE }, body: form });
    const text = await res.text();
    const parsed = text ? safeJson(text) : null;
    if (!res.ok) {
      throw new ApiError(res.status, (parsed as ProblemDetails) ?? { type: '/errors/upload', title: 'Upload failed', status: res.status, detail: text.slice(0, 300) });
    }
    return (parsed ?? null) as T;
  },
};

export interface Query<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  /** True while a refresh is in flight on top of existing data (used to avoid flashing skeletons). */
  refreshing: boolean;
  refetch: () => Promise<void>;
}

/** Minimal data-fetching hook: no cache library, one in-flight request per key, abort on unmount. */
export function useQuery<T>(path: string | null, options: { intervalMs?: number; initialData?: T } = {}): Query<T> {
  const [data, setData] = useState<T | null>(options.initialData ?? null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const [refreshing, setRefreshing] = useState(false);
  const controller = useRef<AbortController | null>(null);
  const mounted = useRef(true);

  // `hasData` is a ref rather than a dependency on purpose: `data` changing must not recreate `load`,
  // or every successful fetch would schedule the next one and the page would poll itself into a loop.
  const hasData = useRef(data !== null);
  hasData.current = data !== null;

  const load = useCallback(async () => {
    if (path === null) return;
    controller.current?.abort();
    const ctrl = new AbortController();
    controller.current = ctrl;
    const hadData = hasData.current;
    setRefreshing(hadData);
    setLoading(!hadData);
    try {
      const result = await api.get<T>(path, ctrl.signal);
      if (!mounted.current) return;
      setData(result);
      setError(null);
    } catch (err) {
      if (!mounted.current || (err instanceof DOMException && err.name === 'AbortError')) return;
      setError(err instanceof ApiError ? err : new ApiError(0, { type: '/errors/network', title: 'Network error', status: 0, detail: err instanceof Error ? err.message : 'the request did not complete' }));
    } finally {
      if (mounted.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [path]);

  useEffect(() => {
    mounted.current = true;
    void load();
    return () => {
      mounted.current = false;
      controller.current?.abort();
    };
  }, [load]);

  useEffect(() => {
    if (!options.intervalMs || path === null) return;
    const timer = setInterval(() => void load(), options.intervalMs);
    return () => clearInterval(timer);
  }, [options.intervalMs, path, load]);

  return useMemo(() => ({ data, error, loading, refreshing, refetch: () => load() }), [data, error, loading, refreshing, load]);
}

/** Session-scoped identity; every page reads its site and role from here. */
export interface Me {
  id: string;
  email: string | null;
  displayName: string | null;
  picture: string | null;
  role: string;
  sites: Array<{ siteId: string; name: string; role: string }>;
  activeSiteId: string | null;
  roleOnActiveSite: string | null;
  entitlement: unknown;
}

export const useMe = () => useQuery<Me>('/me');

export const canEdit = (role: string | null | undefined): boolean => ['admin', 'reliability_engineer', 'analyst'].includes(String(role));
export const canAdmin = (role: string | null | undefined): boolean => role === 'admin';
export const isAnalyst = (role: string | null | undefined): boolean => ['admin', 'reliability_engineer', 'analyst'].includes(String(role));
